import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";

import { formatDollarsRound } from "./pricing";
import type { TypeSummary } from "./types";

// Card for a single piece of equipment, used on the category landing
// page and the search results page. Click-anywhere navigates to the
// equipment detail; the entire card is the link (with a focusable inner heading
// for AT users). Photo is 4:3 per brand guidance, neutral background
// fallback when `featured_image_url` is null (FR-10 edge case).
export interface EquipmentCardProps {
  type: TypeSummary;
  categorySlug: string;
  className?: string;
}

export function EquipmentCard({ type, categorySlug, className }: EquipmentCardProps): JSX.Element {
  return (
    <article
      className={cn(
        "group relative flex h-full flex-col rounded-md border border-zinc-200 bg-brand-white overflow-hidden",
        "focus-within:ring-2 focus-within:ring-brand-red focus-within:ring-offset-2",
        className
      )}
    >
      <div className="aspect-[4/3] w-full overflow-hidden bg-zinc-100">
        {type.featured_image_url ? (
          <img
            src={type.featured_image_url}
            alt=""
            loading="lazy"
            decoding="async"
            className="h-full w-full object-cover transition-transform duration-150 motion-safe:group-hover:scale-[1.02]"
          />
        ) : (
          <PhotoPlaceholder />
        )}
      </div>
      <div className="flex flex-1 flex-col gap-1 p-3">
        <h3 className="text-base font-semibold text-brand-black line-clamp-2">
          <Link
            to={`/c/${categorySlug}/${type.slug}`}
            className="focus-visible:outline-none"
            // The whole card is clickable via the absolutely-positioned
            // overlay below; this <Link> exists for keyboard/AT users.
          >
            <span aria-hidden="true" className="absolute inset-0" />
            {type.name}
          </Link>
        </h3>
        {type.summary ? (
          <p className="text-sm text-zinc-600 line-clamp-2">{type.summary}</p>
        ) : null}
        <p className="mt-auto pt-2 text-sm text-zinc-700">
          {type.from_price_per_week_cents != null
            ? <>from <span className="font-semibold text-brand-black">{formatDollarsRound(type.from_price_per_week_cents)}</span> / week</>
            : <span className="text-zinc-500">Contact for pricing</span>}
        </p>
      </div>
    </article>
  );
}

function PhotoPlaceholder(): JSX.Element {
  return (
    <div
      role="img"
      aria-label="JDR equipment photo coming soon"
      className="flex h-full w-full items-center justify-center bg-zinc-100 text-zinc-400"
    >
      <span className="text-2xl font-bold tracking-tight">JDR</span>
    </div>
  );
}
